import { useState, useEffect } from 'react';
import { apiClient } from '../contexts/AuthContext';
import {
  Brain, AlertTriangle, Lightbulb, Calendar, Target, MessageCircle,
  ChevronDown, ChevronUp, Loader2, RefreshCw, Flame, Dumbbell, Moon, Heart
} from 'lucide-react';

const SEVERITY_STYLES = {
  high: 'bg-red-500/10 border-red-500/30 text-red-400',
  medium: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
  low: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
};

const CATEGORY_ICONS = {
  nutrition: Flame,
  training: Dumbbell,
  recovery: Moon,
  mindset: Heart,
  body: Target,
};

export function CoachingInsights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showPlan, setShowPlan] = useState(false);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/coaching/insights');
      setInsights(data.insights || data);
    } catch {
      setInsights(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="coaching-loading">
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-[#FF6B35] animate-spin" />
          <span className="text-xs text-zinc-500">Analyse en cours...</span>
        </div>
      </div>
    );
  }

  if (!insights) return null;

  const score = insights.score_global || 0;
  const scoreColor = score >= 70 ? '#FF6B35' : score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden" data-testid="coaching-insights">
      {/* Header with score */}
      <div className="p-4 border-b border-[#2A2A2E]/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-sm font-bold text-white">Coach Namaste 🙏</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchInsights} className="p-1 text-zinc-600 hover:text-zinc-400" data-testid="refresh-coaching">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-1.5">
              <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center" style={{ borderColor: scoreColor }}>
                <span className="font-mono text-sm font-bold" style={{ color: scoreColor }}>{score}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Coach message */}
        {insights.message_coach && (
          <p className="text-xs text-zinc-300 leading-relaxed italic" data-testid="coach-message">
            "{insights.message_coach}"
          </p>
        )}
      </div>

      {/* Warnings */}
      {insights.warnings?.length > 0 && (
        <div className="p-3 space-y-2 border-b border-[#2A2A2E]/50">
          {insights.warnings.map((w, i) => (
            <div key={`warn-${w.type}-${i}`} className={`flex items-start gap-2 p-2 border rounded-sm text-xs ${SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.low}`}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{w.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Objective */}
      {insights.objectif_principal && (
        <div className="px-4 py-3 border-b border-[#2A2A2E]/50">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#FF6B35]" />
            <span className="text-xs font-medium text-[#FF6B35]">{insights.objectif_principal}</span>
          </div>
        </div>
      )}

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 flex items-center justify-between text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        data-testid="expand-coaching"
      >
        <span className="flex items-center gap-1.5">
          <Lightbulb className="w-3.5 h-3.5" />
          {insights.conseils?.length || 0} conseils personnalisés
        </span>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-[#2A2A2E]/50">
          {/* Conseils */}
          {insights.conseils?.length > 0 && (
            <div className="p-3 space-y-2">
              {insights.conseils.map((c, i) => {
                const Icon = CATEGORY_ICONS[c.category] || Lightbulb;
                return (
                  <div key={`conseil-${c.category}-${i}`} className="flex items-start gap-2.5 p-2 bg-[#0F0F0F] border border-[#2A2A2E]/50 rounded-sm">
                    <div className={`w-6 h-6 rounded-sm flex items-center justify-center flex-shrink-0 ${
                      c.priority === 'high' ? 'bg-[#FF6B35]/20' : 'bg-zinc-800'
                    }`}>
                      <Icon className={`w-3.5 h-3.5 ${c.priority === 'high' ? 'text-[#FF6B35]' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">{c.title}</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{c.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Plan de la semaine toggle */}
          {insights.plan_semaine?.length > 0 && (
            <div className="border-t border-[#2A2A2E]/50">
              <button
                onClick={() => setShowPlan(!showPlan)}
                className="w-full px-4 py-2 flex items-center justify-between text-xs text-zinc-500 hover:text-zinc-300"
                data-testid="toggle-plan"
              >
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Plan de la semaine
                </span>
                {showPlan ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showPlan && (
                <div className="p-3 space-y-1.5">
                  {insights.plan_semaine.map((day, i) => (
                    <div key={`plan-${day.jour}`} className="flex items-center gap-3 py-1.5 border-b border-[#2A2A2E]/30 last:border-0">
                      <span className="text-[10px] text-zinc-600 w-14 font-medium">{day.jour}</span>
                      <div>
                        <span className="text-xs text-white font-medium">{day.focus}</span>
                        {day.detail && <p className="text-[10px] text-zinc-500">{day.detail}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
